import { API_CONFIG } from '../config/api';

export type ApiError = {
  message: string;
  status: number;
  code?: string;
};

export type ApiResponse<T = any> = {
  data: T;
  message?: string;
  success: boolean;
};

export type RequestConfig = RequestInit & {
  params?: Record<string, string | number | boolean>;
  requiresAuth?: boolean;
};

class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('admin_access_token');
    } catch {
      return null;
    }
  }

  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(endpoint, this.baseURL);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private getHeaders(config: RequestConfig = {}): HeadersInit {
    // Build headers as a plain object for easier manipulation
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Merge any existing headers from config
    if (config.headers) {
      if (config.headers instanceof Headers) {
        config.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(config.headers)) {
        config.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, config.headers);
      }
    }

    // Add auth token if required and available
    if (config.requiresAuth !== false) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers as HeadersInit;
  }

  private handleUnauthorized(): void {
    // Clear authentication data from localStorage
    this.clearAuthToken();
    
    // Clear user info from localStorage
    try {
      localStorage.removeItem('admin_user_info');
    } catch (error) {
      console.warn('Failed to clear user info:', error);
    }

    // Clear React Query cache if available
    try {
      // Check if we're in a browser environment and queryClient is available
      if (typeof window !== 'undefined') {
        // Dispatch a custom event that can be listened to by React Query
        window.dispatchEvent(new CustomEvent('admin:unauthorized'));
      }
    } catch (error) {
      console.warn('Failed to dispatch unauthorized event:', error);
    }

    // Redirect to login page
    // Use window.location.href for a full page reload to ensure clean state
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      // Handle 401 Unauthorized (JWT expired or invalid)
      if (response.status === 401) {
        this.handleUnauthorized();
      }

      let errorMessage = 'An error occurred';
      let errorCode: string | undefined;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        errorCode = errorData.code;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      const error: ApiError = {
        message: errorMessage,
        status: response.status,
        code: errorCode,
      };

      throw error;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    try {
      return await response.json();
    } catch {
      return {} as T;
    }
  }

  private async makeRequest<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { params, requiresAuth, ...requestConfig } = config;

    const url = this.buildURL(endpoint, params);
    const headers = this.getHeaders(config);

    const requestInit: RequestInit = {
      ...requestConfig,
      headers,
    };

    // Add timeout using AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...requestInit,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return this.handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          const timeoutError: ApiError = {
            message: 'Request timeout',
            status: 408,
            code: 'TIMEOUT',
          };
          throw timeoutError;
        }

        if ('status' in error) {
          throw error; // Re-throw API errors
        }
      }

      // Handle network errors
      const networkError: ApiError = {
        message: 'Network error. Please check your connection.',
        status: 0,
        code: 'NETWORK_ERROR',
      };
      throw networkError;
    }
  }

  // HTTP Methods
  async get<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any, config: RequestConfig = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'DELETE',
    });
  }

  // Utility methods
  setAuthToken(token: string): void {
    try {
      localStorage.setItem('admin_access_token', token);
    } catch (error) {
      console.warn('Failed to store auth token:', error);
    }
  }

  clearAuthToken(): void {
    try {
      localStorage.removeItem('admin_access_token');
    } catch (error) {
      console.warn('Failed to clear auth token:', error);
    }
  }

  hasAuthToken(): boolean {
    return !!this.getAuthToken();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
